import { Injectable } from "@angular/core";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { map, switchMap, catchError, tap } from "rxjs/operators";

import * as chartActions from "../actions/chart.actions";
import * as fromServices from "../../services";
import { Router } from "@angular/router";

@Injectable()
export class ChartEffects {
  constructor(
    private actions$: Actions,
    private chartService: fromServices.KpiChartService,
    private router: Router,
  ) {}

  loadCharts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chartActions.LOAD_CHARTS),
      switchMap(() =>
        this.chartService.renderCharts().pipe(
          map(
            (charts) =>
              new chartActions.LoadChartsSuccess(charts.sort((a, b) => a.sortIndex - b.sortIndex)),
          ),
          catchError((error) => of(new chartActions.LoadChartsFail({ error }))),
        ),
      ),
    ),
  );

  loadStaticCharts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chartActions.LOAD_STATIC_CHART),
      switchMap(({ id, frequency }) =>
        this.chartService.getStaticChart(id, frequency).pipe(
          map((chart) => new chartActions.LoadStaticChartSuccess(chart)),
          catchError((error) => of(new chartActions.LoadStaticChartFail({ error }))),
        ),
      ),
    ),
  );

  createChart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(chartActions.CREATE_CHART),
      map((action: chartActions.CreateChart) => action.payload),
      switchMap((chart) => {
        return this.chartService.createChart(chart).pipe(
          map((chart) => new chartActions.CreateChartSuccess(chart)),
          catchError((error) => of(new chartActions.CreateChartFail(error))),
        );
      }),
    );
  });

  updateChart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(chartActions.UPDATE_CHART),
      map((action: chartActions.UpdateChart) => action),
      switchMap(({ id, payload }) => {
        return this.chartService.updateChart(id, payload).pipe(
          map((chart) => new chartActions.UpdateChartSuccess(chart)),
          catchError((error) => of(new chartActions.UpdateChartFail(error))),
        );
      }),
    );
  });

  deleteChart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(chartActions.DELETE_CHART),
      map((action: chartActions.DeleteChart) => action),
      switchMap(({ id, endpointUrl }) => {
        return this.chartService.deleteChart(id, endpointUrl).pipe(
          map((chart) => new chartActions.DeleteChartSuccess(id)),
          catchError((error) => of(new chartActions.DeleteChartFail(error))),
        );
      }),
    );
  });

  reorderCharts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chartActions.REORDER_CHARTS),
      switchMap(({ payload }) =>
        this.chartService.updateSortIndex(payload).pipe(
          map((order) => new chartActions.ReorderChartsSuccess(order)),
          catchError((error) => of(new chartActions.ReorderChartsFail({ error }))),
        ),
      ),
    ),
  );

  chartSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          chartActions.CREATE_CHART_SUCCESS,
          chartActions.UPDATE_CHART_SUCCESS,
          chartActions.DELETE_CHART_SUCCESS,
        ),
        tap(() => this.router.navigate(["/charts"])),
      ),
    { dispatch: false },
  );

  chartFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          chartActions.CREATE_CHART_FAIL,
          chartActions.UPDATE_CHART_FAIL,
          chartActions.DELETE_CHART_FAIL,
        ),
        tap(({ error }) => alert(`Failed to create/update/delete chart: ${error}`)),
      ),
    { dispatch: false },
  );
}
